/*
 * Lightweight JWT + session-mode helper for GetPaid (B2C).
 *
 * Mirrors apps/dashboard/src/lib/auth.ts: a Bearer JWT is stashed in
 * localStorage and its presence is treated as "authenticated". Real session
 * lifecycle (refresh, expiry, logout) is owned by apps/api — JWT_SIGNING_KEY
 * is rotated there.
 *
 * GetPaid additionally pins the org to a single configured mode
 * (VITE_CARATUVA_MODE) — unlike the dashboard, there is no test/live UI
 * toggle here. `ensureConfiguredMode` is called by every login flow right
 * after a token lands so the session always matches the app's build-time mode.
 */

const TOKEN_KEY = 'caratuva.jwt';

export function getToken(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(TOKEN_KEY);
}

export function isAuthenticated(): boolean {
  return Boolean(getToken()) && !isTokenExpired();
}

export type OrgMode = 'test' | 'live';
export type UserRole = 'admin' | 'treasurer' | 'viewer' | 'ops';

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as Record<
      string,
      unknown
    >;
  } catch {
    return null;
  }
}

// Decode the `role` claim from the stored JWT. Used only for client-side UX
// gating — the real authorization is enforced server-side regardless.
export function getRole(): UserRole | null {
  const token = getToken();
  if (!token) return null;
  const json = decodeJwtPayload(token);
  const role = json?.role;
  if (role === 'admin' || role === 'treasurer' || role === 'viewer' || role === 'ops') {
    return role;
  }
  return null;
}

// Decode the `mode` claim from the stored JWT. The token is the single source
// of truth for the active mode (set on consume, re-minted by switch-mode), so
// we read it back rather than tracking mode separately. Defaults to `test`.
export function getMode(): OrgMode {
  const token = getToken();
  if (!token) return 'test';
  const json = decodeJwtPayload(token);
  return json?.mode === 'live' ? 'live' : 'test';
}

// Decode the `exp` claim (seconds since epoch) from the stored JWT.
export function getTokenExpiry(): number | null {
  const token = getToken();
  if (!token) return null;
  const json = decodeJwtPayload(token);
  const exp = json?.exp;
  return typeof exp === 'number' ? exp : null;
}

export function isTokenExpired(): boolean {
  const exp = getTokenExpiry();
  if (exp === null) return false;
  return Date.now() >= exp * 1000;
}

// The mode this GetPaid deployment is configured for. Defaults to 'test' so
// a missing env var never accidentally exposes live money movement. Flip to
// 'live' for production launch (see .env.example).
export function configuredMode(): OrgMode {
  return import.meta.env.VITE_CARATUVA_MODE === 'live' ? 'live' : 'test';
}

// Called by every login flow right after a token is stashed via setToken.
// If the freshly-minted session's mode doesn't match this deployment's
// configured mode, re-mint the JWT via switch-mode and persist the result.
// GetPaid never shows a mode switcher — this keeps the session silently in
// lockstep with how the app is deployed.
export async function ensureConfiguredMode(): Promise<void> {
  const desired = configuredMode();
  if (getMode() === desired) return;
  const { switchMode } = await import('./api');
  const result = await switchMode(desired);
  setToken(result.token);
}
